import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Upload, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface ImageUploadProps {
    bucket: string;
    path: string; // The folder path to upload to (e.g., 'userId/packages/packageId')
    currentImages: string[];
    onUpload: (urls: string[]) => void;
    maxFiles?: number;
    maxSizeInMB?: number;
}

export default function ImageUpload({
    bucket,
    path,
    currentImages,
    onUpload,
    maxFiles = 5,
    maxSizeInMB = 5
}: ImageUploadProps) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const files = Array.from(e.target.files);
        if (currentImages.length + files.length > maxFiles) {
            toast.error(`You can only upload up to ${maxFiles} images.`);
            return;
        }

        setUploading(true);
        const newUrls: string[] = [];

        try {
            for (const file of files) {
                if (file.size > maxSizeInMB * 1024 * 1024) {
                    toast.error(`File ${file.name} is too large. Max size is ${maxSizeInMB}MB.`);
                    continue;
                }

                if (!file.type.startsWith('image/')) {
                    toast.error(`File ${file.name} is not an image.`);
                    continue;
                }

                const fileExt = file.name.split('.').pop();
                const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
                const filePath = `${path}/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from(bucket)
                    .upload(filePath, file);

                if (uploadError) throw uploadError;

                const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
                newUrls.push(data.publicUrl);
            }

            if (newUrls.length > 0) {
                onUpload([...currentImages, ...newUrls]);
                toast.success('Images uploaded successfully!');
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            toast.error(error.message || 'Failed to upload images');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeImage = (indexToRemove: number) => {
        onUpload(currentImages.filter((_, idx) => idx !== indexToRemove));
    };

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {currentImages.map((url, idx) => (
                    <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                        <img
                            src={url}
                            alt={`Uploaded ${idx + 1}`}
                            className="w-full h-full object-cover"
                        />
                        <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X size={14} />
                        </button>
                    </div>
                ))}

                {currentImages.length < maxFiles && (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="flex flex-col items-center justify-center aspect-square rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-rose-500 dark:hover:border-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/10 transition-colors"
                    >
                        {uploading ? (
                            <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
                        ) : (
                            <>
                                <Upload className="w-6 h-6 text-gray-400 mb-2" />
                                <span className="text-sm text-gray-500 dark:text-gray-400">Upload Image</span>
                            </>
                        )}
                    </button>
                )}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
                {currentImages.length} / {maxFiles} images uploaded. Max size {maxSizeInMB}MB per image.
            </p>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
            />
        </div>
    );
}
